import { SyntheticEvent, useMemo, useState } from "react"
import { makeStyles } from "tss-react/mui"
import { Button, FormControlLabel, Grid, Switch } from "@mui/material"
import useActiveFiltersCountAccounting from "../../../../../../shared/components/filters/accountingFiltersDrawer/useActiveFiltersCountAccounting"
import { StandardNoResultText } from "../../../../../../shared/components/noResult/StandardNoResultText"
import { StandardFilterButton } from "../../../../../../shared/components/table/StandardFilterButton"
import {
    AccountingFilterProps,
    AccountingFiltersDrawer,
    defaultAccountingFilterOptionsList,
} from "../../../../../../shared/components/filters/accountingFiltersDrawer/AccountingFiltersDrawer"
import { useAppLocalization } from "../../../../../localization/components/AppLocalizationProvider"
import {
    DateType,
    SelectDateTypeFilter,
} from "../../../../../../shared/components/filters/selectDateTypeFilter/SelectDateTypeFilter"
import { LoadingSpinner } from "../../../../../../shared/components/loading/LoadingSpinner"
import { LocalizedStrict } from "../../../../../localization/components/AppLocalized"
import { AccountingQuery, getAccountingCustomers } from "../../accountingApi"
import { useInitialDateFilters } from "../../../../../../shared/components/table/useInitialDateFilters"
import { useMerchant } from "../../../../../merchants/merchantHooks"
import { useApiQuery } from "../../../../../../core/api/useApiQuery"
import { ExportButtonDebtors } from "../ExportButtonDebtors"
import { AccountingDebtor } from "../../accountingTypes"
import { CustomerAccordion } from "./CustomerAccordion"
import { useOrderTypeFilter } from "../../../../../../shared/components/filters/orderTypeFilter/useOrderTypeState"
import { StandardDateRangeFilter, type DtDateRange } from "../../../../../../shared/components/table/StandardDateRangeFilter"

const useStyles = makeStyles()((theme) => ({
    dateFilter: {
        backgroundColor: theme.palette.common.white,
    },
    linkButton: {
        fontSize: ".875rem",
        color: theme.palette.primary.main,
        textDecoration: "underline",
        textTransform: "none",
    },
    dateRangeFilter: {
        background: "white",
    },
}))

interface ExpandedState {
    [key: string]: boolean
}

type CustomerAccountingTabProps = Readonly<{}>

export function CustomerAccountingTab({}: CustomerAccountingTabProps) {
    const { classes } = useStyles()
    const { l10n } = useAppLocalization()
    const { merchantId } = useMerchant()
    const { startDate, endDate } = useInitialDateFilters()
    const [drawerOpen, setDrawerOpen] = useState(false)
    const [dateType, setDateType] = useState<DateType>(DateType.ORDER_DATE)
    const { orderType, setOrderType } = useOrderTypeFilter()

    const [expanded, setExpanded] = useState<ExpandedState>({})
    const [onlyWithSales, setOnlyWithSales] = useState<boolean>(false)

    const [filtersList, setFiltersList] = useState<AccountingFilterProps>({
        ...defaultAccountingFilterOptionsList,
        merchantId: merchantId.toString(),
    })

    const [dateState, setDateState] = useState<DtDateRange>({
        startDate,
        endDate,
    })

    const { activeCount } = useActiveFiltersCountAccounting(filtersList)

    const customerReport = useApiQuery(getAccountingCustomers, {
        queryName: AccountingQuery.CUSTOMERS,
        dependencies: {
            ...filtersList,
            merchantId,
            hasSales: onlyWithSales ? true : undefined,
            merchantIds: filtersList.merchantIds?.join(","),
            shopIds: filtersList.shopIds?.join(","),
            paymentMethods: filtersList.paymentMethods?.join(","),
            startDate: dateState.startDate?.toISODate(),
            endDate: dateState.endDate?.toISODate(),
            dateType,
            orderType,
        },
    })

    const debtors: AccountingDebtor[] = useMemo(() => {
        if (!customerReport.data) return []

        return customerReport.data.customers.flatMap((customer) => {
            if (customer.isOfficeHotel && customer.userGroups) {
                const groupDebtors: AccountingDebtor[] = customer.userGroups.map((customerGroup) => ({
                    type: "customerGroup",
                    id: customerGroup.id,
                    customerId: customer.id,
                    name: customerGroup.name,
                    isActive: customerGroup.isActive,
                    hasSales: customerGroup.hasSales,
                }))

                return groupDebtors
            } else {
                const customerDebtor: AccountingDebtor = {
                    type: "customer",
                    id: customer.id,
                    name: customer.name,
                    isActive: customer.isActive,
                    hasSales: customer.hasSales ?? false,
                }

                return customerDebtor
            }
        })
    }, [customerReport.data])

    const handleChange = (debtor: AccountingDebtor) => (event: SyntheticEvent, isExpanded: boolean) => {
        setExpanded((prev) => ({
            ...prev,
            [debtor.name]: isExpanded,
        }))
    }

    const handleExpandAll = () => {
        const allExpanded = debtors.reduce((acc, debtor) => {
            const panelId = debtor.name
            acc[panelId] = true
            return acc
        }, {} as ExpandedState)
        setExpanded(allExpanded)
    }

    const handleCollapseAll = () => {
        const allCollapsed = debtors.reduce((acc, debtor) => {
            const panelId = debtor.name
            acc[panelId] = false
            return acc
        }, {} as ExpandedState)
        setExpanded(allCollapsed)
    }

    const isAllExpanded = debtors.every((debtor) => expanded[debtor.name])

    return (
        <>
            <Grid container wrap="nowrap" alignItems="center" width="100%" paddingBottom="2rem">
                <Grid container gap="1rem" flexWrap="nowrap">
                    <SelectDateTypeFilter
                        dateType={dateType}
                        setDateType={setDateType}
                        className={classes.dateFilter}
                    />
                    <StandardDateRangeFilter
                        dateFilterClasses={classes.dateRangeFilter}
                        value={dateState}
                        onChange={setDateState}
                    />

                    <FormControlLabel
                        control={<Switch checked={onlyWithSales} onClick={() => setOnlyWithSales(!onlyWithSales)} />}
                        label={l10n.getString("customer-accounting-show-customers-with-sales-only")}
                    />
                </Grid>
                <Grid container width="auto" justifyContent="flex-end" gap="1rem" flexWrap="nowrap">
                    <StandardFilterButton onClick={() => setDrawerOpen(true)} activeCount={activeCount} bordered />

                    {merchantId && (
                        <ExportButtonDebtors
                            merchantId={merchantId}
                            startDate={dateState.startDate.toISODate()}
                            endDate={dateState.endDate.toISODate()}
                            filtersList={filtersList}
                            debtors={debtors}
                            dateType={dateType}
                        />
                    )}
                </Grid>
            </Grid>

            {!customerReport.data?.customers && !customerReport.isFetching && (
                <Grid paddingBottom={"1rem"}>
                    <StandardNoResultText />
                </Grid>
            )}

            <Grid paddingBottom="1rem">
                {isAllExpanded ? (
                    <LocalizedStrict id="accounting-customers-button-collapse-all">
                        <Button variant="text" className={classes.linkButton} onClick={handleCollapseAll}>
                            Collapse all
                        </Button>
                    </LocalizedStrict>
                ) : (
                    <LocalizedStrict id="accounting-customers-button-expand-all">
                        <Button variant="text" className={classes.linkButton} onClick={handleExpandAll}>
                            Expand all
                        </Button>
                    </LocalizedStrict>
                )}
            </Grid>

            {customerReport.isLoading && <LoadingSpinner />}

            {debtors.map((debtor) => (
                <CustomerAccordion
                    key={debtor.id}
                    debtor={debtor}
                    startDate={dateState.startDate}
                    endDate={dateState.endDate}
                    shopIds={filtersList.shopIds?.join(",")}
                    dateType={dateType}
                    expanded={expanded[debtor.name] ?? false}
                    handleChange={handleChange}
                />
            ))}
            <AccountingFiltersDrawer
                setFiltersList={setFiltersList}
                drawerOpen={drawerOpen}
                setDrawerOpen={setDrawerOpen}
                filtersList={filtersList}
            />
        </>
    )
}
